import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorGroup, Label, Spacer } from "./EditorHelpers";
import styled from "styled-components";
import { FlyerType } from "../FlyerTypePicker";
import { useFlyerConfig } from "../util/useFlyerConfig";
import { useRestorable, v } from "../util/useRestorable";
import { PresetTextEditor } from "../components/TextEditor";
import { useIsHardNo } from "../util/useDomain";
import { countryFromLocale } from "../locales/locales";



export interface FooterProps {
  leftText: string;
  rightText: string;
  imageUrl?: string;
}

interface FooterEditorProps {
  onChange: (update: FooterProps) => void;
}

const getDefaultImageUrl = (flyerType: FlyerType, country: string) => {
  if (flyerType !== FlyerType.BOYCOTT) {
    return null;
  }
  return country === "CA"
    ? "/canada_map_green.png"
    : "/europe_map_green.png";
}

export default function FooterEditor(props: FooterEditorProps) {
  const { t } = useTranslation();
  const { onChange } = props;
  const { flyerLocale, flyerType } = useFlyerConfig();
  const { t: dt } = useTranslation(null, { lng: flyerLocale.substring(0, 2) })
  const { isHardNo } = useIsHardNo();
  const [leftText, setLeftText] = useRestorable(flyerType !== FlyerType.GREEN ? dt('default.left-footer') : "", 'leftText');
  const [rightText, setRightText] = useRestorable(isHardNo ? 'hardno.eu' : 'boycott-flyer.eu', 'rightText');
  const [imageUrl, setImageUrl] = useState<string | null>(getDefaultImageUrl(flyerType, countryFromLocale(flyerLocale)));
  const [debouncedChange, setDebouncedChange] = useState<FooterProps | null>(null);

  // Debounce changes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedChange({
        leftText: v(leftText),
        rightText: v(rightText),
        imageUrl
      });
    }, 250);

    // Cleanup function to clear the timeout
    return () => {
      clearTimeout(handler);
    };
  }, [leftText, rightText, imageUrl]);

  useEffect(() => {
    onChange(debouncedChange)
  }, [debouncedChange]);
  
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageUrl(URL.createObjectURL(file));
    }
  }

  return (
    <EditorGroup>
      <PresetTextEditor 
        label={t('left-footer-text.label')}
        value={leftText}
        onChange={setLeftText} 
      />
      <Spacer />
      <PresetTextEditor 
        label={t('right-footer-text.label')}
        value={rightText}
        onChange={setRightText} 
      />
      {flyerType !== FlyerType.GREEN && <>
        <Spacer />
        <Label>{t('footer-image.label')}</Label>
        <input type="file" accept="image/*" onChange={handleImageChange} />
        {imageUrl && <Image src={imageUrl} alt="Selected" />}
      </>}
    </EditorGroup>
  )
}

const Image = styled.img`
  max-width: 100%;
  max-height: 100px;
  object-fit: contain;
`;